import update from 'immutability-helper';

import {
   ADD_MODULE,
   ADD_SCORE,
   COMPLETE_SESSION,
   INIT,
   PREPARE_NEXT_CHALLENGE,
   PREPARE_NEXT_SESSION,
   REFRESH_MODULE_QUEUE,
   REFRESH_CURRENT_MODULE,
   REPLACE_EXERCISE_DOCUMENT,
   SET_MODULE_LOADING,
   SET_METADATA,
   SET_PROGRESS,
   SET_PROMPT,
   SET_SELECTED_MODULES,
   SET_STAGE,
   START_NEXT_CHALLENGE,
   START_NEXT_SESSION,
} from './actions';
import { exerciseStages } from '../shared/constants';
import { COMPLETE_TUTORIAL } from '../tutorial/actions';
import { calculateAverage } from './interval/util';
import { getRandomArrayElement } from '../util/getRandomArrayElement';
import { getSessionsAndAverageScore } from './getSessionsAndAverageScore';

const exerciseReducer = (
   state = {
      type: '',
      interfaceType: '',
      levelSelect: null,
      nextConfig: null,
      progress: {
         currentLevel: 1,
         tutorialComplete: false,
         sessionHistory: [],
      },
      settings: {
         challengesPerSession: 5,
      },
      currentStage: exerciseStages.welcome,
      preparingSession: false,
      preparingChallenge: false,
      hasLeveledUp: false,
      scores: [],
      modules: {},
      moduleQueue: [],
      currentModuleId: null,
      displayModules: false,
   },
   action
) => {
   switch (action.type) {
      case INIT:
         return {
            ...state,
            progress: action.savedProgress,
            exerciseDocument: action.exerciseDocument,
            type: action.exerciseDocument.exerciseControlType,
            interfaceType: action.exerciseDocument.userIterfaceType,
            levelSelect: action.levelSelect,
            nextConfig: action.nextConfig,
         };

      case REPLACE_EXERCISE_DOCUMENT:
         return {
            ...state,
            modules: {},
            exerciseDocument: action.exerciseDocument,
         };

      case PREPARE_NEXT_SESSION:
         return {
            ...state,
            preparingSession: true,
         };

      case START_NEXT_SESSION:
         return {
            ...state,
            preparingSession: false,
            nextConfig: state.nextConfig
               ? {
                    ...state.nextConfig,
                    remainingSessionCount:
                       state.nextConfig.remainingSessionCount - 1,
                 }
               : null,
         };

      case PREPARE_NEXT_CHALLENGE:
         return {
            ...state,
            preparingChallenge: true,
         };

      case START_NEXT_CHALLENGE:
         return {
            ...state,
            preparingChallenge: false,
         };

      case SET_STAGE:
         return { ...state, currentStage: action.stage };

      case SET_PROGRESS:
         return { ...state, progress: action.progress };

      case ADD_SCORE:
         return update(state, {
            scores: {
               $push: [action.score],
            },
         });

      case COMPLETE_SESSION: {
         // If they have selected to play a certain level, should not update progress
         if (state.levelSelect != null) {
            return update(state, {
               currentStage: { $set: exerciseStages.sessionOverview },
               scores: { $set: [] },
               levelSelect: {
                  lastAverageScore: { $set: calculateAverage(state.scores) },
               },
            });
         }

         const activeExercise = state.currentModuleId
            ? state.modules[state.currentModuleId]
            : state;

         const sessionHistory = [
            ...activeExercise.progress.sessionHistory,
            {
               level: action.isSubscribed
                  ? activeExercise.progress.currentLevel
                  : Math.min(
                       activeExercise.progress.currentLevel,
                       activeExercise.exerciseDocument.maxUnpaidLevel
                    ),
               score: calculateAverage(state.scores),
               timestamp: new Date().toISOString(),
            },
         ];
         if (sessionHistory.length > 30) {
            sessionHistory.shift();
         }

         const { sessionsAtLevel, averageSessionScore } =
            getSessionsAndAverageScore(activeExercise, sessionHistory);

         const lvlUpRequirements =
            activeExercise.exerciseDocument.levels[
               activeExercise.progress.currentLevel - 1
            ]?.lvlUpRequirements;
         const shouldLevelUp =
            !!lvlUpRequirements &&
            averageSessionScore >= lvlUpRequirements.minAvgScore &&
            sessionsAtLevel >= lvlUpRequirements.minSessions;

         const progress = {
            currentLevel: {
               $set:
                  activeExercise.progress.currentLevel +
                  (shouldLevelUp ? 1 : 0),
            },
            tutorialComplete: {
               $set: shouldLevelUp
                  ? false
                  : activeExercise.progress.tutorialComplete,
            },
            sessionHistory: {
               $set: sessionHistory,
            },
         };

         const progressUpdateObject = state.currentModuleId
            ? {
                 modules: {
                    [state.currentModuleId]: { progress },
                 },
              }
            : { progress };

         return update(state, {
            ...progressUpdateObject,
            currentStage: { $set: exerciseStages.sessionOverview },
            hasLeveledUp: { $set: shouldLevelUp },
            scores: { $set: [] },
         });
      }

      case COMPLETE_TUTORIAL: {
         // If they have selected to play a tutorial for a certain level, should not update progress
         if (state.levelSelect != null) {
            return update(state, {
               levelSelect: {
                  tutorial: { $set: false },
               },
            });
         }

         if (state.currentModuleId) {
            return update(state, {
               modules: {
                  [state.currentModuleId]: {
                     progress: { tutorialComplete: { $set: true } },
                  },
               },
            });
         }
         return update(state, {
            progress: { tutorialComplete: { $set: true } },
         });
      }

      case SET_MODULE_LOADING: {
         return update(state, {
            modules: {
               [action.id]: {
                  $set: { loading: true },
               },
            },
         });
      }

      case ADD_MODULE: {
         return update(state, {
            modules: {
               [action.moduleData.exerciseDocument.id]: {
                  $set: action.moduleData,
               },
            },
         });
      }

      case SET_SELECTED_MODULES: {
         return update(state, {
            progress: {
               selectedModules: { $set: action.selectedModules },
               modulesSelectedTimestamp: { $set: new Date().toISOString() },
            },
         });
      }

      case REFRESH_MODULE_QUEUE: {
         if (
            state.progress.selectedModules == null ||
            state.progress.selectedModules.length === 0
         ) {
            return state;
         }

         const newQueue = action.clearCurrent ? [] : [...state.moduleQueue];

         while (newQueue.length < action.count) {
            newQueue.push(
               getRandomArrayElement(state.progress.selectedModules)
            );
         }

         return { ...state, moduleQueue: newQueue };
      }

      case REFRESH_CURRENT_MODULE: {
         const [nextCurrentAux, ...newQueue] = state.moduleQueue;

         // get random if nothing is in queue
         if (nextCurrentAux == null) {
            return {
               ...state,
               currentModuleId: getRandomArrayElement(
                  state.progress.selectedModules
               ),
            };
         }

         return {
            ...state,
            currentModuleId: nextCurrentAux,
            moduleQueue: newQueue,
         };
      }

      case SET_METADATA:
         return { ...state, metadata: action.metadata };

      case SET_PROMPT:
         return { ...state, prompt: action.prompt };

      default:
         return state;
   }
};

export { exerciseReducer };
